import React, { useMemo, useState } from "react";
import { Container, Overline, useCollection } from "@/components/common";
import PageHero from "@/components/PageHero";
import { getContent } from "@/lib/api";
import { MagnifyingGlass, Copy, ArrowSquareOut, DownloadSimple, GraduationCap } from "@phosphor-icons/react";
import { toast } from "sonner";

function citation(p) {
  return `${p.authors}. ${p.title}. ${p.journal}. ${p.year}.${p.doi ? " doi:" + p.doi : ""}`;
}

function PubCard({ p }) {
  const copy = () => { navigator.clipboard.writeText(citation(p)); toast.success("Citation copied"); };
  return (
    <div data-testid="publication-card" className="border-b border-slate-200 py-7 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded bg-navy px-2 py-0.5 text-white">{p.year}</span>
        <span className="text-gold-dark">{p.journal}</span>
        {p.publication_type && <span className="text-softgrey-400">· {p.publication_type}</span>}
      </div>
      <h3 className="mt-2 font-serif text-xl leading-snug text-navy">{p.title}</h3>
      <p className="mt-1.5 text-sm text-softgrey-600">{p.authors}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {p.cancer_type && <span className="rounded-full border border-slate-300 px-2.5 py-0.5 text-[11px] text-navy/70">{p.cancer_type}</span>}
        {p.research_area && <span className="rounded-full border border-slate-300 px-2.5 py-0.5 text-[11px] text-navy/70">{p.research_area}</span>}
      </div>
      <div className="mt-4 flex flex-wrap gap-4 text-xs font-semibold">
        <button data-testid="copy-citation" onClick={copy} className="flex items-center gap-1.5 text-navy/70 hover:text-gold-dark"><Copy size={14} /> Copy citation</button>
        <a href={p.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-navy/70 hover:text-gold-dark"><ArrowSquareOut size={14} /> Open publication</a>
      </div>
    </div>
  );
}

export default function Research() {
  const { data, loading } = useCollection(() => getContent("publications"), []);
  const { data: themes } = useCollection(() => getContent("research_themes"), []);
  const [q, setQ] = useState("");
  const [year, setYear] = useState("");
  const [cancer, setCancer] = useState("");
  const [area, setArea] = useState("");
  const [sort, setSort] = useState("year_desc");

  const years = useMemo(() => Array.from(new Set(data.map((p) => p.year))).sort().reverse(), [data]);
  const cancers = useMemo(() => Array.from(new Set(data.map((p) => p.cancer_type).filter(Boolean))).sort(), [data]);

  const filtered = useMemo(() => {
    let r = data.filter((p) => {
      const t = `${p.title} ${p.authors} ${p.journal}`.toLowerCase();
      return (!q || t.includes(q.toLowerCase())) && (!year || p.year === year) && (!cancer || p.cancer_type === cancer) && (!area || p.research_area === area);
    });
    r = [...r].sort((a, b) => sort === "year_desc" ? b.year.localeCompare(a.year) : sort === "year_asc" ? a.year.localeCompare(b.year) : a.title.localeCompare(b.title));
    return r;
  }, [data, q, year, cancer, area, sort]);

  const exportCitations = () => {
    const blob = new Blob([filtered.map(citation).join("\n\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "sirohi-publications.txt"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Citations exported");
  };

  return (
    <div data-testid="research-page">
      <PageHero label="Research & Publications" title="A Body of Research Advancing Global Oncology"
        intro="A searchable database of Dr. Sirohi's peer-reviewed publications, guidelines and reviews across GI and breast oncology, global health and health equity."
        image="https://images.unsplash.com/photo-1773984203620-e73698b3fbe5?crop=entropy&cs=srgb&fm=jpg" />

      <section className="border-b border-slate-200 bg-softgrey-100 py-8">
        <Container>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-6">
              <div><div className="font-serif text-3xl text-navy">{data.length}+</div><div className="text-xs text-softgrey-600">Selected Publications</div></div>
              <div><div className="font-serif text-3xl text-navy">25</div><div className="text-xs text-softgrey-600">h-index</div></div>
              <div><div className="font-serif text-3xl text-navy">{themes.length}</div><div className="text-xs text-softgrey-600">Research Themes</div></div>
            </div>
            <a href="https://scholar.google.com/" target="_blank" rel="noopener noreferrer" data-testid="google-scholar-link" className="flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-700">
              <GraduationCap size={18} /> Google Scholar Profile
            </a>
          </div>
        </Container>
      </section>

      <section className="bg-white py-14">
        <Container>
          <div className="grid gap-10 lg:grid-cols-12">
            <aside className="lg:col-span-3">
              <div className="sticky top-28 space-y-6">
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-3 text-softgrey-400" />
                  <input data-testid="pub-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full rounded-md border border-slate-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-gold" />
                </div>
                {[["Year", year, setYear, years], ["Cancer Type", cancer, setCancer, cancers], ["Research Area", area, setArea, themes.map((t) => t.name)]].map(([lbl, val, set, opts]) => (
                  <div key={lbl}>
                    <Overline className="text-gold-dark">{lbl}</Overline>
                    <select data-testid={`pub-filter-${lbl.toLowerCase().replace(/\s/g, "-")}`} value={val} onChange={(e) => set(e.target.value)} className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-gold">
                      <option value="">All</option>
                      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
                <button data-testid="export-citations" onClick={exportCitations} className="flex w-full items-center justify-center gap-2 rounded-md border border-navy/25 px-4 py-2.5 text-sm font-semibold text-navy hover:bg-navy hover:text-white">
                  <DownloadSimple size={16} /> Export citations
                </button>
              </div>
            </aside>
            <div className="lg:col-span-9">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-softgrey-600">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</p>
                <select data-testid="pub-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-gold">
                  <option value="year_desc">Newest first</option>
                  <option value="year_asc">Oldest first</option>
                  <option value="title">Title A–Z</option>
                </select>
              </div>
              {loading ? <p className="py-16 text-center text-softgrey-600">Loading…</p> :
                filtered.length === 0 ? <p className="py-16 text-center text-softgrey-600">No publications match your filters.</p> :
                <div>{filtered.map((p) => <PubCard key={p.id} p={p} />)}</div>}
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
